// The comparable range BEFORE the picked one, for the "vs. last period" delta
// on ניתוח רכש.
//
// Two rules, and both exist because the naive version lies.
//
// 1. LIKE FOR LIKE ON ELAPSED DAYS. On 26 August, "August" means 26 days of
//    spend. Comparing it against all 31 days of July reported −55% on real
//    production data — a collapse nobody caused, on the screen a manager
//    checks most often. So the previous window is truncated to the SAME number
//    of elapsed days: 1–26 August against 1–26 July.
//
// 2. A WHOLE MONTH COMPARES TO THE WHOLE MONTH BEFORE IT. The page's picker is
//    month-based, so "last month" is what the comparison means. A plain
//    "same length immediately before" rule gets this right for August (31 days
//    back from 1 August is 1 July) and wrong for July (31 days back from
//    1 July is 31 MAY, because June has 30) — coincidentally right, which is
//    worse than consistently wrong. Whole calendar months are detected and
//    mapped to the previous calendar month; anything else falls back to the
//    same length immediately before, which is the only meaning an arbitrary
//    range has.
//
// Local-calendar arithmetic via setDate/setMonth, never toISOString: the latter
// is UTC, so a boundary would slip a day for anyone east of Greenwich — the
// same trap targetPeriod.js documents for period keys. setDate handles month
// and year rollover on its own.
const ISO = /^\d{4}-\d{2}-\d{2}$/;

const ymd = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const parse = (s) => new Date(`${s}T00:00:00`);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const lastOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const dayCount = (a, b) => Math.round((b - a) / 86400000) + 1;

/** Is [f, t] exactly one whole calendar month? */
const isWholeMonth = (f, t) =>
  f.getDate() === 1
  && f.getFullYear() === t.getFullYear()
  && f.getMonth() === t.getMonth()
  && t.getDate() === lastOfMonth(f).getDate();

/**
 * @param {string} from ISO `YYYY-MM-DD`
 * @param {string} to   ISO `YYYY-MM-DD`
 * @param {Date}   now  injectable for tests
 * @returns {{from: string, to: string}|null} null when no honest comparison
 *   exists — the caller shows the figure with no delta rather than a
 *   comparison against a window it made up.
 *
 * NOTE the CURRENT side is not truncated here: the caller's figure covers the
 * whole picked range. That is equivalent as long as no document is dated in the
 * future, which is the normal case; a future-dated invoice would sit in the
 * current figure with no counterpart in the previous window.
 */
export function previousRange(from, to, now = new Date()) {
  if (!ISO.test(from || '') || !ISO.test(to || '')) return null;
  const f = parse(from);
  const t = parse(to);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime()) || t < f) return null;

  // A range entirely in the future has no elapsed days to compare.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (today < f) return null;

  const elapsed = dayCount(f, t < today ? t : today);

  let prevFrom, prevWindowEnd;
  if (isWholeMonth(f, t)) {
    prevFrom = new Date(f.getFullYear(), f.getMonth() - 1, 1);
    prevWindowEnd = lastOfMonth(prevFrom);
  } else {
    prevFrom = addDays(f, -dayCount(f, t));
    prevWindowEnd = addDays(f, -1);
  }

  // Clamped: February cannot lend 31 days to a comparison with March, and
  // without this the previous window would spill into the current one and
  // double-count days that are already in the figure above it.
  const wanted = addDays(prevFrom, elapsed - 1);
  const prevTo = wanted > prevWindowEnd ? prevWindowEnd : wanted;
  return { from: ymd(prevFrom), to: ymd(prevTo) };
}

/**
 * Percent change from `prev` to `curr`, or null when it cannot be stated.
 *
 * A previous period of 0 has no percentage — "up ∞%" is not a fact a card can
 * print — so the caller falls back to showing no delta at all. That is the
 * common case for a restaurant's first month, which is exactly when a wrong
 * number would be most confusing.
 */
export function changePct(curr, prev) {
  if (!prev || prev <= 0 || curr == null) return null;
  return Math.round(((curr - prev) / prev) * 1000) / 10;
}
